import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react'
import { useMedplumProfile } from '@medplum/react'
import { useSubscription } from '@medplum/react-hooks'
import { useMiddlewareClient } from '@/hooks'
import { useClient } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'
import {
  getCommunicationFromSubscriptionBundle,
  getParticipantLastDeliveredAt,
  type MiddlewareThreadSummary,
} from './service'

type MessagesUnreadContextValue = {
  hasUnreadMessages: boolean
  /**
   * Publish the flag from a view that already loaded the full thread list, so
   * the indicator updates without a second request. Only valid for an
   * *unfiltered* list — see the note in `useMessagesPageState`.
   */
  setHasUnreadMessages: (value: boolean) => void
  refreshUnreadState: () => Promise<void>
}

// Keep unread state shared outside the Messages page.
// The shell nav and message subscriptions both need the same value, including when the
// user is on a different tab and the Messages page is not mounted.
const defaultMessagesUnreadContextValue: MessagesUnreadContextValue = {
  hasUnreadMessages: false,
  setHasUnreadMessages: () => undefined,
  refreshUnreadState: () => Promise.resolve(),
}

const MessagesUnreadContext = createContext<MessagesUnreadContextValue>(
  defaultMessagesUnreadContextValue,
)

export const MessagesUnreadProvider = ({ children }: PropsWithChildren) => {
  const middleware = useMiddlewareClient()
  const orpc = useClient()
  const profile = useMedplumProfile()
  const practitionerProfile = profile?.resourceType === 'Practitioner' ? profile : undefined
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false)

  /**
   * Tell senders their messages arrived on this device.
   *
   * This provider already reloads the thread list on mount and on every
   * incoming message, so hooking the acknowledgement here covers the live case
   * and the back-from-offline case with one code path — a client that was away
   * simply acknowledges late, on its next load. No queue, no retry: a failed
   * call is picked up by the next reload.
   *
   * The unacknowledged set is computed client-side so the common case — nothing
   * new — sends no request at all.
   */
  const acknowledgeDelivery = useCallback(
    async (threads: MiddlewareThreadSummary[], practitionerId: string): Promise<void> => {
      const threadIds = threads
        .filter((item) => {
          const latestSentAt = item.latestMessage?.sent
          const latestSenderId = item.latestMessage?.sender?.reference?.replace('Practitioner/', '')
          if (!item.thread.id || !latestSentAt || latestSenderId === practitionerId) {
            return false
          }

          const deliveredThrough = getParticipantLastDeliveredAt(item.thread, practitionerId)
          return !deliveredThrough || deliveredThrough < latestSentAt
        })
        .map((item) => item.thread.id as string)

      if (threadIds.length === 0) {
        return
      }

      try {
        await orpc.acknowledgeThreadDelivery({ threadIds })
      } catch (err) {
        // Fire-and-forget: the next reload retries this for free.
        logError('messages', 'Failed to acknowledge message delivery', err)
      }
    },
    [orpc],
  )

  const refreshUnreadState = useCallback(async (): Promise<void> => {
    const practitionerId = practitionerProfile?.id
    if (!practitionerId) {
      setHasUnreadMessages(false)
      return
    }

    try {
      const threads = (await middleware.get('/threads')) as MiddlewareThreadSummary[]
      setHasUnreadMessages(threads.some((thread) => thread.status === 'unread'))
      await acknowledgeDelivery(threads, practitionerId)
    } catch (err) {
      logError('messages', 'Failed to refresh unread state', err)
    }
  }, [acknowledgeDelivery, middleware, practitionerProfile?.id])

  useEffect(() => {
    refreshUnreadState().catch((err) =>
      logError('messages', 'Failed to refresh unread state on mount', err),
    )
  }, [refreshUnreadState])

  // Catch up on anything the subscription missed — a dropped WebSocket, a
  // suspended tab, or a machine that was asleep. Without this the indicator can
  // sit stale until the next full page load.
  useEffect(() => {
    const handleVisible = (): void => {
      if (document.visibilityState !== 'visible') {
        return
      }

      refreshUnreadState().catch((err) =>
        logError('messages', 'Failed to refresh unread state on tab focus', err),
      )
    }

    document.addEventListener('visibilitychange', handleVisible)
    window.addEventListener('focus', handleVisible)
    return () => {
      document.removeEventListener('visibilitychange', handleVisible)
      window.removeEventListener('focus', handleVisible)
    }
  }, [refreshUnreadState])

  useSubscription(
    practitionerProfile?.id
      ? `Communication?recipient=Practitioner/${practitionerProfile.id}`
      : undefined,
    (bundle) => {
      if (!getCommunicationFromSubscriptionBundle(bundle)) {
        return
      }

      refreshUnreadState().catch((err) =>
        logError('messages', 'Failed to refresh unread state on subscription', err),
      )
    },
  )

  const value = useMemo(
    () => ({
      hasUnreadMessages,
      setHasUnreadMessages,
      refreshUnreadState,
    }),
    [hasUnreadMessages, refreshUnreadState],
  )

  return <MessagesUnreadContext.Provider value={value}>{children}</MessagesUnreadContext.Provider>
}

export { MessagesUnreadContext }
